import {AxiosPromise } from 'axios';
import { injectable } from 'inversify';
import { Data, Model, Utils } from '@singularsystems/neo-core';
import { AppService, Types } from '../../App/Services/AppService';
import MissingAccountLookup from '../ClientAccountMapping/Models/MissingAccountLookup';
import CommandResult from '../../Common/Models/CommandResult';
import ClientAccountSearchCriteria from '../ClientAccountMapping/Models/ClientAccountSearchCriteria';
import MissingAccountsSearchCriteria from '../ClientAccountMapping/Models/MissingAccountsSearchCriteria';
import CreateExcludedAccountCommand from '../ExclusionAccounts/Models/Commands/CreateExcludedAccountCommand';
import ExcludedAccountSearchCriteria from '../ExclusionAccounts/Models/Queries/ExcludedAccountSearchCriteria';
import ExcludedAccountLookup from '../ExclusionAccounts/Models/Lookups/ExcludedAccountLookup';
import CreateClientCommand from '../ClientAccountMapping/Models/CreateClientCommand';
import ClientLookup from '../ClientAccountMapping/Models/ClientLookup';
import ClientAccountLookup from '../ClientAccountMapping/Models/ClientAccountLookup';
import ClientNameAndNumberLookup from '../ClientAccountMapping/Models/ClientNameAndNumberLookup';
import ClientEntityTypeLookup from '../ClientAccountMapping/Models/ClientEntityTypeLookup';
import UpdateClientCommand from '../ClientAccountMapping/Models/UpdateClientCommand';

export interface IClientAccountsApiClient {
    createClientAccountMapping(command: Model.PlainObject<CreateClientCommand>): AxiosPromise<Model.PlainObject<CommandResult>>;
    createExcludedAccount(command: Model.PlainObject<CreateExcludedAccountCommand>): AxiosPromise<Model.PlainObject<CommandResult>>;
    getClientAccountLookupListByCriteria(criteria?: string): AxiosPromise<Array<Model.PlainObject<ClientLookup>>>;
    getClientAccountNumbersListByCriteria(criteria: string, fileGroupingID: number): AxiosPromise<Array<Model.PlainObject<ClientAccountLookup>>>;
    getClientLookupById(clientAccountID: number): AxiosPromise<Model.PlainObject<ClientLookup>>;
    getClientAccountLookupByID(clientAccountID: number): AxiosPromise<Model.PlainObject<ClientAccountLookup>>;
    getPagedClientAccounts(payload: Model.PartialPlainNonTrackedObject<Data.PageRequest<ClientAccountSearchCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<ClientLookup>>>;
    getPagedExcludedAccounts(payload: Model.PartialPlainNonTrackedObject<Data.PageRequest<ExcludedAccountSearchCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<ExcludedAccountLookup>>>;
    getPagedMissingAccounts(payload: Model.PartialPlainNonTrackedObject<Data.PageRequest<MissingAccountsSearchCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<MissingAccountLookup>>>;
    getClientNameAndNumber(searchCriteria: string): AxiosPromise<Model.PlainObject<ClientNameAndNumberLookup>>;
    getClientEntityTypeListByCriteria(searchCritera: string): AxiosPromise<Array<Model.PlainObject<ClientEntityTypeLookup>>>;
    updateAndSaveClient(command: Model.PlainObject<UpdateClientCommand>): AxiosPromise<Model.PlainObject<CommandResult>>;

}

@injectable()
export default class ClientAccountApiClient extends Data.ApiClientBase implements IClientAccountsApiClient {
    constructor(config = AppService.get(Types.Config)) {
        super(`${config.apiPath}/ClientAccountMapping`)
    }

    public createClientAccountMapping(command: Model.PlainObject<CreateClientCommand>): AxiosPromise<Model.PlainObject<CommandResult>> {
        return this.axios.post(`${this.apiPath}/createClientAccountMapping`, command);
    }

    public createExcludedAccount(command: Model.PlainObject<CreateExcludedAccountCommand>): AxiosPromise<Model.PlainObject<CommandResult>> {
        return this.axios.post(`${this.apiPath}/AddNewExcludedAccount`, command);
    }

    public getClientAccountLookupListByCriteria(criteria: string = ""): AxiosPromise<Array<Model.PlainObject<ClientLookup>>> {
        return this.axios.get(`${this.apiPath}/GetClientAccountLookupListByCriteria`, { params: { criteria: criteria } });
    }

    public getClientAccountNumbersListByCriteria(criteria: string, fileGroupingID: number): AxiosPromise<Array<Model.PlainObject<ClientAccountLookup>>> {
        return this.axios.get(`${this.apiPath}/GetClientAccountNumbersByCriteria`, { params: { searchCriteria: criteria, fileGroupingID: fileGroupingID }});
    }

    public getClientLookupById(clientId: number): AxiosPromise<Model.PlainObject<ClientLookup>> {
        return this.axios.get(`${this.apiPath}/GetClientLookupById`, { params: { clientId: clientId }});
    }
    
    public getPagedClientAccounts(payload:  Model.PartialPlainNonTrackedObject<Data.PageRequest<ClientAccountSearchCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<ClientLookup>>> {
        return this.axios.get(`${this.apiPath}/GetClientAccountList?${Utils.getQueryString(payload)}`);
    }

    public getPagedExcludedAccounts(payload: Model.PartialPlainNonTrackedObject<Data.PageRequest<ExcludedAccountSearchCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<ExcludedAccountLookup>>> {
        return this.axios.get(`${this.apiPath}/GetExcludedAccountsPagedList?${Utils.getQueryString(payload)}`);
    }

    public getPagedMissingAccounts(payload: Model.PartialPlainNonTrackedObject<Data.PageRequest<MissingAccountsSearchCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<MissingAccountLookup>>> {
        return this.axios.get(`${this.apiPath}/GetMissingAccountList?${Utils.getQueryString(payload)}`);
    }

    public getClientAccountLookupByID(clientAccountID: number): AxiosPromise<Model.PlainObject<ClientAccountLookup>> {
        return this.axios.get(`${this.apiPath}/GetClientAccountLookupForID`, { params: { clientAccountID: clientAccountID }});
    }

    public getClientNameAndNumber(searchCriteria: string): AxiosPromise<Model.PlainObject<ClientNameAndNumberLookup>>{
        return this.axios.get(`${this.apiPath}/GetClientNameAndPolicyNumber?accountNumber=${searchCriteria}`);
    }

    public getClientEntityTypeListByCriteria(searchCritera: string): AxiosPromise<Array<Model.PlainObject<ClientEntityTypeLookup>>> {
        return this.axios.get(`${this.apiPath}/GetClientEntityTypeList`, { params: { searchCritera: searchCritera } });
    }

    public updateAndSaveClient(command: Model.PlainObject<UpdateClientCommand>): AxiosPromise<Model.PlainObject<CommandResult>> {
        return this.axios.post(`${this.apiPath}/UpdateAndSaveClient`, command);
    }
}